// =============================================================================
// SimSense Platform — Lap Comparison Page
// =============================================================================

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { MultiLapGraph } from "../components/telemetry/MultiLapGraph";
import { TrackMap } from "../components/telemetry/TrackMap";
import { api } from "../utils/api";
import { formatLapTime } from "../utils/formatters";
import { generateDistanceAxis, interpolateArray } from "../utils/telemetryMath";

import { useUnits } from "../hooks/useUnits";

type TelemetryHoverDetail = {
  idx: number | null;
  lapDistPct: number | null;
};

export function LapComparison() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const units = useUnits();
  
  const [session, setSession] = useState<any>(null);
  const [manifest, setManifest] = useState<any>(null);
  const [trackModel, setTrackModel] = useState<any>(null);
  const [referenceLapInfo, setReferenceLapInfo] = useState<any>(null);
  const [lapsData, setLapsData] = useState<any[]>([]);
  const [selectedLaps, setSelectedLaps] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const hoverFrameRef = useRef<number | null>(null);
  const latestHoverRef = useRef<TelemetryHoverDetail>({ idx: null, lapDistPct: null });

  // OT-117: Decouple hover from React state entirely to prevent auto-scroll and re-renders.
  // Hover updates dispatch a custom DOM event; only consumer components (TrackMap, SteeringHUD) subscribe.
  const handleHover = useCallback((detail: TelemetryHoverDetail) => {
    latestHoverRef.current = detail;
    if (hoverFrameRef.current !== null) return;
    hoverFrameRef.current = window.requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      window.dispatchEvent(new CustomEvent("telemetry-hover", { detail: latestHoverRef.current }));
    });
  }, []);

  useEffect(() => () => {
    if (hoverFrameRef.current !== null) {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
  }, []);

  // OT-315: Shared zoom state across all graphs (Motec-style drag-to-zoom)
  const [zoomRange, setZoomRange] = useState<[number, number] | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { min, max } = (e as CustomEvent).detail;
      // Only set zoom if the range is a subset (user dragged to zoom in)
      if (min != null && max != null && (max - min) < 0.99) {
        setZoomRange([min, max]);
      }
    };
    window.addEventListener("telemetry-zoom", handler);
    return () => window.removeEventListener("telemetry-zoom", handler);
  }, []);

  const resetZoom = useCallback(() => {
    setZoomRange(null);
    // Broadcast reset to all graphs
    window.dispatchEvent(new CustomEvent("telemetry-zoom", {
      detail: { min: 0, max: 1, source: "__reset__" },
    }));
  }, []);

  // OT-098: Reference lap from another session
  const refSessionId = searchParams.get("refSession");
  const refLapNum = searchParams.get("refLap");
  const [refData, setRefData] = useState<any>(null);
  const [refInfo, setRefInfo] = useState<any>(null);

  // Initial lap selection from URL if present
  useEffect(() => {
    const lapsStr = searchParams.get("laps");
    if (lapsStr) {
      setSelectedLaps(lapsStr.split(",").map(Number));
    }
  }, [searchParams]);

  useEffect(() => {
    async function fetchMetadata() {
      try {
        const [sData, mData] = await Promise.all([
          api<any>(`/api/sessions/${id}`),
          api<any>(`/api/sessions/${id}/lap-manifest`),
        ]);
        setSession(sData);
        setManifest(mData);
        document.title = `Lap Compare — ${sData.trackName || "Session"} | SimSense`;

        // OT-112: Fetch reference lap info for sidebar button
        try {
          const ref = await api<any>(`/api/leaderboard/reference?track=${encodeURIComponent(sData.trackName)}&car=${encodeURIComponent(sData.carName)}&exclude_session_id=${id}`);
          setReferenceLapInfo(ref);
        } catch { /* no ref */ }

        if (sData.trackId) {
          const tData = await api<any>(`/api/tracks/${sData.trackId}`);
          setTrackModel(tData);
        }
      } catch (err) {
        console.error("Failed to fetch manifest or track model", err);
      }
    }
    fetchMetadata();
  }, [id]);

  // OT-098: Fetch reference lap telemetry
  useEffect(() => {
    if (!refSessionId || !refLapNum) return;
    async function fetchRef() {
      try {
        const [tele, info] = await Promise.all([
          api<any>(`/api/sessions/${refSessionId}/laps/${refLapNum}/telemetry?reference=true`),
          api<any>(`/api/leaderboard/reference?track=${encodeURIComponent(session?.trackName || "")}&car=${encodeURIComponent(session?.carName || "")}`),
        ]);
        setRefData(tele);
        setRefInfo(info);
      } catch (err) {
        console.error("Failed to fetch reference lap", err);
      }
    }
    if (session) fetchRef();
  }, [refSessionId, refLapNum, session]);

  useEffect(() => {
    async function fetchTelemetry() {
      if (selectedLaps.length === 0) {
        setLapsData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await Promise.all(
          selectedLaps.map(globalLap =>
            api<any>(`/api/sessions/${id}/laps/${globalLap}/telemetry`)
          )
        );
        setLapsData(data);
      } catch (err) {
        console.error("Failed to fetch telemetry", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTelemetry();
  }, [id, selectedLaps]);

  const toggleLap = (globalLap: number) => {
    setSelectedLaps(prev => 
      prev.includes(globalLap) 
        ? prev.filter(l => l !== globalLap)
        : [...prev, globalLap].slice(-6) // Limit to 6 laps
    );
  };

  const addReferenceLap = () => {
    if (!referenceLapInfo) return;
    setSearchParams(prev => {
      prev.set("refSession", referenceLapInfo.session_id);
      prev.set("refLap", referenceLapInfo.global_lap.toString());
      return prev;
    });
    setRefData(null);
    setRefInfo(null);
  };

  const removeReferenceLap = () => {
    setSearchParams(prev => {
      prev.delete("refSession");
      prev.delete("refLap");
      return prev;
    });
    setRefData(null);
    setRefInfo(null);
  };

  // OT-111: Pre-interpolate all telemetry data to a common 1000-point axis
  const interpolatedLaps = useMemo(() => {
    const rawLaps = refData ? [...lapsData, refData] : lapsData;
    if (rawLaps.length === 0) return [];

    const x = generateDistanceAxis(1000);
    const channelsToInterpolate = ["Speed", "Throttle", "Brake", "SteeringWheelAngle", "Lat", "Lon", "LapDist"];

    return rawLaps.map(lap => {
      const rawDistPct = lap.LapDistPct || lap.channels?.LapDistPct?.values || [];
      const interpolated: any = { LapDistPct: x };

      if (rawDistPct.length === 0) {
        channelsToInterpolate.forEach(chan => {
          interpolated[chan] = new Array(x.length).fill(0);
        });
        return interpolated;
      }

      // OT-134: Normalize LapDistPct to 0-1 range. API may return 0-100 (percentage).
      const maxDist = rawDistPct[rawDistPct.length - 1];
      const distPct = maxDist > 1.5 ? rawDistPct.map((v: number) => v / 100) : rawDistPct;

      channelsToInterpolate.forEach(chan => {
        const raw = lap[chan] || lap.channels?.[chan]?.values || [];
        if (raw.length > 0) {
          interpolated[chan] = interpolateArray(distPct, raw, x);
        } else {
          interpolated[chan] = new Array(x.length).fill(0);
        }
      });
      return interpolated;
    });
  }, [lapsData, refData]);

  // OT-124 fix: useMemo hooks MUST be called before any early return (React Rules of Hooks)
  const comparedSetups = useMemo(() => {
    if (!manifest) return [];
    const setups = new Set<string>();
    // OT-217: Extract just the .sto filename (basename) from full path
    const basename = (path: string) => {
      const parts = path.replace(/\\/g, "/").split("/");
      return parts[parts.length - 1];
    };
    selectedLaps.forEach(gl => {
      const l = manifest.laps.find((lap: any) => lap.globalLap === gl);
      if (l) {
        const s = manifest.stints.find((st: any) => st.stintIndex === l.stintIndex);
        if (s?.setupName && s.setupName !== "Unknown") {
          setups.add(basename(s.setupName));
        }
      }
    });
    if (refInfo?.setup_name) {
      setups.add(basename(refInfo.setup_name));
    }
    // OT-217: Fallback to session-level setup name from metadata
    if (setups.size === 0 && session?.metadata?.SetupName && session.metadata.SetupName !== "Unknown") {
      setups.add(basename(session.metadata.SetupName));
    }
    return Array.from(setups);
  }, [selectedLaps, manifest, refInfo, session]);

  // OT-217: Show event type (Race, Practice, etc.) from session level — this is what users care about
  const eventType = useMemo(() => {
    return session?.eventType || null;
  }, [session]);

  if (!session || !manifest) return <div style={{ padding: "40px", textAlign: "center" }}>Loading session...</div>;

  const allLapLabels = [
    ...selectedLaps.map(gl => {
      const l = manifest.laps.find((lap: any) => lap.globalLap === gl);
      return `Lap ${l?.lapNum || gl}`;
    }),
    ...(refData && refInfo ? [`REF: ${refInfo.driver || "Fastest"} (${formatLapTime(refInfo.lap_time)})`] : []),
  ];
  const graphColors = refData
    ? [...selectedLaps.map((_, i) => getLapColor(i)), "#d4a017"]
    : undefined;
  const trackMapColors = graphColors ?? selectedLaps.map((_, i) => getLapColor(i));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 320px) minmax(0, 1fr)", gap: "16px" }}>

      {/* Sidebar: Lap Selector */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: "calc(100vh - 80px)", position: "sticky", top: "16px", paddingRight: "8px" }}>
        {/* Breadcrumbs + Title — inside sidebar to avoid dead space in right column */}
        <div>
          <div style={{ fontSize: "12px", color: "var(--text2)", marginBottom: "4px" }}>
            <Link to="/" style={{ color: "var(--accent)", textDecoration: "none" }}>Sessions</Link>
            {" / "}
            <Link to={`/session/${id}`} style={{ color: "var(--accent)", textDecoration: "none" }}>{session.trackName}</Link>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h2 style={{ margin: 0, fontSize: "16px" }}>Lap Comparison</h2>
            <span style={{ fontSize: "11px", color: "var(--text3)" }}>{selectedLaps.length}/6</span>
          </div>
        </div>

          {/* OT-112: Reference Lap Integration */}
          {referenceLapInfo && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <h3 style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--text2)" }}>Community Benchmark</h3>
              {!refData ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <button 
                    onClick={addReferenceLap}
                    style={{
                      padding: "12px",
                      background: "linear-gradient(135deg, rgba(212,160,23,0.1), rgba(184,134,11,0.05))",
                      border: "1px solid rgba(212,160,23,0.3)",
                      borderRadius: "var(--radius-sm)",
                      color: "#d4a017",
                      fontSize: "13px",
                      fontWeight: 700,
                      cursor: "pointer",
                      textAlign: "center"
                    }}
                  >
                    ✨ Compare to Fastest Driver
                  </button>
                  {referenceLapInfo.is_user_best && (
                    <div style={{ fontSize: "11px", color: "var(--accent)", fontWeight: 600, textAlign: "center" }}>
                      You are the fastest driver!
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: "12px",
                  background: "rgba(212,160,23,0.15)",
                  border: "1px solid #d4a017",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#d4a017" }}>Reference Active</div>
                  <button 
                    onClick={removeReferenceLap}
                    style={{ background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: "11px", fontWeight: 600 }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minHeight: 0 }}>
            <h3 style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--text2)", margin: 0, flexShrink: 0 }}>Select Laps</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", overflowY: "auto", flex: 1, paddingRight: "4px" }}>
            {manifest.laps.map((lap: any) => {
              const isSelected = selectedLaps.includes(lap.globalLap);
              
              return (
                <div 
                  key={lap.globalLap}
                  onClick={() => toggleLap(lap.globalLap)}
                  style={{
                    padding: "10px 12px",
                    background: isSelected ? "rgba(59, 130, 246, 0.1)" : "var(--card)",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontWeight: 600, fontSize: "13px" }}>Lap {lap.lapNum}</div>
                      <div style={{ fontSize: "11px", color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>{formatLapTime(lap.lapTime)}</div>
                    </div>
                  </div>
                  {isSelected && (
                    <div style={{ marginLeft: "8px", width: "8px", height: "8px", borderRadius: "50%", background: getLapColor(selectedLaps.indexOf(lap.globalLap)) }} />
                  )}
                </div>
              );
            })}
            </div>
          </div>

        </div>

        {/* Main: Graphs */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflowY: "auto" }}>
          {interpolatedLaps.length > 0 && (
            <TrackMapWrapper
              telemetry={interpolatedLaps[0]}
              telemetries={interpolatedLaps}
              selectedLapColors={trackMapColors}
              height={240}
              trackModel={trackModel}
            />
          )}

          {loading ? (
            <div style={{ padding: "80px 40px", textAlign: "center", background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              Loading telemetry data...
            </div>
          ) : interpolatedLaps.length === 0 ? (
            <div style={{ padding: "80px 40px", textAlign: "center", background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              Select up to 6 laps to compare telemetry.
            </div>
          ) : (
            <>
              {/* OT-071: Consolidated Context Header */}
              {(comparedSetups.length > 0 || eventType) && (
              <div style={{
                padding: "12px 20px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
                display: "flex",
                gap: "32px",
                fontSize: "13px"
              }}>
                {comparedSetups.length > 0 && (
                <div>
                  <span style={{ color: "var(--text3)", textTransform: "uppercase", fontSize: "10px", fontWeight: 700, marginRight: "8px", letterSpacing: "0.5px" }}>Setup</span>
                  <span style={{ fontWeight: 600 }}>{comparedSetups.join(", ")}</span>
                </div>
                )}
                {eventType && (
                <div>
                  <span style={{ color: "var(--text3)", textTransform: "uppercase", fontSize: "10px", fontWeight: 700, marginRight: "8px", letterSpacing: "0.5px" }}>Event</span>
                  <span style={{ fontWeight: 600 }}>{eventType}</span>
                </div>
                )}
              </div>
              )}

              {/* OT-098: Reference lap context banner */}
              {refInfo && (
                <div style={{
                  padding: "10px 16px",
                  background: "linear-gradient(135deg, rgba(212,160,23,0.12), rgba(184,134,11,0.08))",
                  border: "1px solid rgba(212,160,23,0.3)",
                  borderRadius: "var(--radius)",
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div>
                    <span style={{ color: "#d4a017", fontWeight: 700 }}>Reference Lap</span>
                    <span style={{ color: "var(--text2)", marginLeft: "12px" }}>
                      {refInfo.driver} — {formatLapTime(refInfo.lap_time)}
                      {refInfo.session_date && ` (${new Date(refInfo.session_date).toLocaleDateString()})`}
                    </span>
                  </div>
                  {manifest.laps.length > 0 && selectedLaps.length > 0 && (() => {
                    const userBestTime = Math.min(...selectedLaps.map(gl => {
                      const l = manifest.laps.find((lap: any) => lap.globalLap === gl);
                      return l?.lapTime || Infinity;
                    }));
                    const delta = userBestTime - refInfo.lap_time;
                    return (
                      <span style={{ fontWeight: 600, color: delta > 0 ? "#ef4444" : "#10b981", fontVariantNumeric: "tabular-nums" }}>
                        {delta > 0 ? "+" : ""}{delta.toFixed(3)}s
                      </span>
                    );
                  })()}
                </div>
              )}

              {/* OT-315: Reset Zoom button — appears when zoomed in */}
              {zoomRange && (
                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "4px 0",
                }}>
                  <button
                    onClick={resetZoom}
                    style={{
                      padding: "5px 14px",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "var(--accent)",
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      borderRadius: "var(--radius-sm)",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = "rgba(59, 130, 246, 0.2)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)")}
                  >
                    Reset Zoom
                  </button>
                </div>
              )}

              <MultiLapGraph
                lapsData={interpolatedLaps}
                lapLabels={allLapLabels}
                channel="Speed"
                height={180}
                onHover={handleHover}
                units={units.system}
                trackModel={trackModel}
                colors={graphColors}
                zoomRange={zoomRange}
              />
              <MultiLapGraph
                lapsData={interpolatedLaps}
                lapLabels={allLapLabels}
                channel="Throttle"
                height={130}
                onHover={handleHover}
                trackModel={trackModel}
                colors={graphColors}
                zoomRange={zoomRange}
              />
              <MultiLapGraph
                lapsData={interpolatedLaps}
                lapLabels={allLapLabels}
                channel="Brake"
                height={130}
                onHover={handleHover}
                trackModel={trackModel}
                colors={graphColors}
                zoomRange={zoomRange}
              />
              
              {/* OT-116: Steering Angle Graph + Inline Steering Wheels */}
              <div style={{ background: "var(--card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", overflow: "hidden" }}>
                <MultiLapGraph
                  lapsData={interpolatedLaps}
                  lapLabels={allLapLabels}
                  channel="SteeringWheelAngle"
                  height={130}
                  onHover={handleHover}
                  trackModel={trackModel}
                  colors={graphColors}
                  embedded
                  zoomRange={zoomRange}
                />
                {/* OT-091: Steering Wheels HUD — isolated from parent hover re-renders */}
                <SteeringHUDPanel
                  interpolatedLaps={interpolatedLaps}
                  selectedLaps={selectedLaps}
                  refData={refData}
                  refInfo={refInfo}
                  manifest={manifest}
                />
              </div>
            </>
          )}
        </div>

      </div>
  );
}

function getLapColor(index: number): string {
  const colors = [
    "#3b82f6", // Blue
    "#ef4444", // Red
    "#10b981", // Green
    "#06b6d4", // Cyan
    "#8b5cf6", // Purple
    "#ec4899", // Pink
  ];
  return colors[index % colors.length];
}

// OT-117: Hook that subscribes to hover events without causing parent re-renders.
// Only the component calling this hook re-renders when hover changes.
function useHoverDetail(): TelemetryHoverDetail {
  const [detail, setDetail] = useState<TelemetryHoverDetail>({ idx: null, lapDistPct: null });
  useEffect(() => {
    const handler = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "number" || next == null) {
        setDetail({ idx: next ?? null, lapDistPct: null });
        return;
      }
      setDetail({
        idx: typeof next?.idx === "number" ? next.idx : null,
        lapDistPct: typeof next?.lapDistPct === "number" ? next.lapDistPct : null,
      });
    };
    window.addEventListener("telemetry-hover", handler);
    return () => window.removeEventListener("telemetry-hover", handler);
  }, []);
  return detail;
}

function useHoverIndex(): number | null {
  return useHoverDetail().idx;
}

function useHoverLapDistPct(): number | null {
  return useHoverDetail().lapDistPct;
}

// OT-117: Isolated wrapper for TrackMap that subscribes to hover without re-rendering parent
function TrackMapWrapper({ telemetry, telemetries, selectedLapColors, height, trackModel }: {
  telemetry: any;
  telemetries?: any[];
  selectedLapColors: string[];
  height: number;
  trackModel?: any;
}) {
  const hoverLapDistPct = useHoverLapDistPct();
  return (
    <TrackMap
      telemetry={telemetry}
      telemetries={telemetries}
      hoverLapDistPct={hoverLapDistPct}
      selectedLapColors={selectedLapColors}
      height={height}
      trackModel={trackModel}
      onClickPct={(pct) => {
        // Dispatch click as a hover event so graphs sync to clicked position
        window.dispatchEvent(new CustomEvent("telemetry-hover", { detail: { lapDistPct: pct } }));
      }}
    />
  );
}

// OT-117: Isolated wrapper for SteeringHUD panel that subscribes to hover without re-rendering parent
function SteeringHUDPanel({ interpolatedLaps, selectedLaps, refData, refInfo, manifest }: {
  interpolatedLaps: any[];
  selectedLaps: number[];
  refData: any;
  refInfo: any;
  manifest: any;
}) {
  const hoverIndex = useHoverIndex();
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "16px",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "4px 16px 10px",
    }}>
      {interpolatedLaps.map((telemetry, i) => {
        if (!telemetry?.SteeringWheelAngle) return null;
        const isRef = refData && i === interpolatedLaps.length - 1;
        const idx = hoverIndex !== null ? Math.min(hoverIndex, telemetry.LapDistPct.length - 1) : null;
        const rawAngle = idx !== null ? telemetry.SteeringWheelAngle[idx] : 0;
        const angleDeg = rawAngle * 57.2958;
        const color = isRef ? "#d4a017" : getLapColor(i);
        const label = isRef
          ? `REF: ${refInfo?.driver || "Fastest"}`
          : `Lap ${manifest.laps.find((l: any) => l.globalLap === selectedLaps[i])?.lapNum || selectedLaps[i]}`;
        return (
          <div key={isRef ? "ref" : selectedLaps[i]} style={{ textAlign: "center", minWidth: "96px" }}>
            <SteeringHUDWheel angle={angleDeg} color={color} size={90} />
            <div style={{ marginTop: "2px", fontSize: "10px", fontWeight: 600, color }}>{label}</div>
            <div style={{ fontSize: "9px", color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>{(-angleDeg).toFixed(1)}°</div>
          </div>
        );
      })}
    </div>
  );
}

// Map lap color hex → preset wheel PNG filename
const WHEEL_MAP: Record<string, string> = {
  "#3b82f6": "/steeringwheel_blue.png",
  "#ef4444": "/steeringwheel_red.png",
  "#10b981": "/steeringwheel_green.png",
  "#06b6d4": "/steeringwheel_cyan.png",
  "#8b5cf6": "/steeringwheel_purple.png",
  "#ec4899": "/steeringwheel_pink.png",
  "#d4a017": "/steeringwheel_gold.png",
};

function SteeringHUDWheel({ angle, color, size = 60 }: { angle: number, color: string, size?: number }) {
  const wheelSrc = WHEEL_MAP[color] || "/steeringwheel_blue.png";
  return (
    <img
      src={wheelSrc}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      style={{
        display: "block",
        transform: `rotate(${-angle}deg)`,
        transition: "transform 0.05s linear",
      }}
    />
  );
}
